/**
 * Size grouping configuration for filtering products
 * Maps display labels to all possible size variations
 */

export interface SizeGroup {
  label: string;
  displayLabel: string;
  sizes: string[];
  sortOrder: number;
}

export const CLOTHING_SIZE_GROUPS: Record<string, SizeGroup> = {
  'xs': {
    label: 'xs',
    displayLabel: 'XS',
    sizes: ['0', '1', '2', 'XS', 'xs', 'extra small'],
    sortOrder: 1
  },
  's': {
    label: 's', 
    displayLabel: 'S',
    sizes: ['3', '4', '5', 'S', 'SM', 's', 'sm', 'small'],
    sortOrder: 2
  },
  'm': {
    label: 'm',
    displayLabel: 'M', 
    sizes: ['6', '7', '8', 'M', 'MED', 'm', 'med', 'medium'],
    sortOrder: 3
  },
  'l': {
    label: 'l',
    displayLabel: 'L',
    sizes: ['9', '10', '11', 'L', 'LG', 'l', 'lg', 'large'],
    sortOrder: 4
  },
  'xl': {
    label: 'xl',
    displayLabel: 'XL',
    sizes: ['12', '13', '14', 'XL', '1XL', 'xl', '1xl', 'xlarge'],
    sortOrder: 5
  },
  'xxl': {
    label: 'xxl',
    displayLabel: 'XXL', 
    sizes: ['15', '16', '17', 'XXL', '2XL', 'xxl', '2xl', 'xxlarge'],
    sortOrder: 6
  },
  'xxxl': {
    label: 'xxxl',
    displayLabel: 'XXXL',
    sizes: ['18', '19', '20', 'XXXL', '3XL', 'xxxl', '3xl', 'xxxlarge'],
    sortOrder: 7
  },
  'xxxxl': {
    label: 'xxxxl', 
    displayLabel: 'XXXXL',
    sizes: ['21', '22', '23', 'XXXXL', '4XL', 'xxxxl', '4xl', 'xxxxlarge'],
    sortOrder: 8
  },
  'onesize': {
    label: 'onesize',
    displayLabel: 'OS',
    sizes: ['One Size', 'one size', 'onesize', 'OS', 'os'],
    sortOrder: 9
  }
};

/**
 * Get all clothing size groups sorted by display order
 * @returns Array of size groups ordered from XS to One Size
 */
export const getClothingSizeGroups = (): SizeGroup[] => {
  return Object.values(CLOTHING_SIZE_GROUPS).sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Check if any product sizes match the sizes in a specific size group
 * @param productSizes - Array of size strings from product variants/tags
 * @param groupKey - Key of the size group to check against (e.g., 'xs', 'sm', 'lg')
 * @returns true if product has any matching sizes in the group
 */
export const productMatchesSizeGroup = (productSizes: string[], groupKey: string): boolean => {
  const group = CLOTHING_SIZE_GROUPS[groupKey];
  if (!group) return false;

  return productSizes.some(productSize => 
    group.sizes.some(groupSize => 
      productSize.toLowerCase().trim() === groupSize.toLowerCase().trim()
    )
  );
};

/**
 * Extract all possible sizes from a Shopify product
 * Searches variant options and product tags for size information
 * @param product - Shopify product object with variants and tags
 * @returns Array of unique size strings found in the product
 */
export const getProductSizes = (product: any): string[] => {
  const sizes: string[] = [];

  // Get sizes from variant options
  if (product.variants) {
    product.variants.forEach((variant: any) => {
      if (variant.selectedOptions) {
        variant.selectedOptions.forEach((option: any) => {
          if (option.name?.toLowerCase().includes('size')) {
            sizes.push(option.value);
          }
        });
      }
    });
  }

  // Get sizes from product tags
  if (product.tags) {
    product.tags.forEach((tag: string) => {
      // Check if tag looks like a size (number, or common size abbreviations)
      const tagLower = tag.toLowerCase().trim();
      if (/^(xs|s|sm|m|med|l|lg|xl|xxl|xxxl|xxxxl|[\d]+|one\s*size|adjustable)$/i.test(tagLower)) {
        sizes.push(tag);
      }
    });
  }

  return [...new Set(sizes)]; // Remove duplicates
};

/**
 * Find which size groups a product belongs to
 * Useful for filtering and categorizing products by size availability
 * @param product - Shopify product object to analyze
 * @returns Array of size group keys that match the product's sizes
 */
export const getProductSizeGroups = (product: any): string[] => {
  const productSizes = getProductSizes(product);
  const matchingGroups: string[] = [];

  Object.keys(CLOTHING_SIZE_GROUPS).forEach(groupKey => {
    if (productMatchesSizeGroup(productSizes, groupKey)) {
      matchingGroups.push(groupKey);
    }
  });

  return matchingGroups;
};